import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, ShieldCheck, ShieldAlert, ExternalLink, Download, Copy, X, Upload } from "lucide-react";
import api from "../lib/api";
import { Layout } from "../components/Layout";
import { SocialPost } from "../components/SocialPost";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ComplianceBadge = ({ compliance }) => {
  const c = compliance || {};
  const ok = c.ownWords && c.sources && c.image;
  return ok ? (
    <span className="inline-flex items-center gap-1 font-ui text-[11px] font-medium text-[#2F6B33] bg-[#EAF3EA] border border-[#BFD9BF] px-2 py-0.5 rounded-full">
      <ShieldCheck className="w-3 h-3" /> Compliant
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 font-ui text-[11px] font-medium text-[#8A5A00] bg-[#FBF1DD] border border-[#E4D3A8] px-2 py-0.5 rounded-full">
      <ShieldAlert className="w-3 h-3" /> Incomplete
    </span>
  );
};

const AdminFacts = () => {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dupes, setDupes] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (ready && !user) nav("/admin", { replace: true });
  }, [ready, user, nav]);

  useEffect(() => {
    api.get("/facts").then((r) => {
      setFacts(r.data.sort((a, b) => a.title.localeCompare(b.title)));
      setLoading(false);
    });
  }, []);

  const exportCurios = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/curios/export", { params: { status: "all" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `curiovault_curios_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Curios exported to CSV");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const runDuplicateCheck = async () => {
    setChecking(true);
    try {
      const r = await api.get("/admin/curios/duplicates");
      setDupes(r.data);
    } catch {
      toast.error("Duplicate check failed");
    } finally {
      setChecking(false);
    }
  };

  const deleteCurio = async (slug) => {
    if (!window.confirm(`Delete "${slug}" permanently? It will be removed from the database and will not re-seed.`)) return;
    try {
      await api.delete(`/admin/curios/${slug}`);
      toast.success("Curio deleted");
      setFacts((fs) => fs.filter((f) => f.slug !== slug));
      const r = await api.get("/admin/curios/duplicates");
      setDupes(r.data);
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="caption-label mb-1">Curator Desk</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#1A1615]">Manage Curios</h1>
          </div>
          <Link
            to="/admin/new"
            data-testid="dashboard-add-fact"
            className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-5 py-2.5 bg-[#9E4535] text-[#FAF7F0] rounded-sm hover:bg-[#82382B] transition-colors self-start"
          >
            <Plus className="w-4 h-4" /> Add New Curio
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <Link to="/admin/curios/workbench" data-testid="nav-curio-workbench" className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-4 py-2 bg-[#9E4535] text-[#FAF7F0] rounded-sm hover:bg-[#82382B] transition-colors">
            <ShieldCheck className="w-4 h-4" /> Curio Workbench
          </Link>
          <Link to="/admin/trivia" data-testid="nav-trivia-workbench" className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm text-[#4A4036] hover:border-[#9E4535] hover:text-[#9E4535] transition-colors">
            <ShieldCheck className="w-4 h-4" /> Trivia Workbench
          </Link>
          <Link to="/admin/curios/workbench?import=1" data-testid="import-curios-btn" className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm text-[#4A4036] hover:border-[#9E4535] hover:text-[#9E4535] transition-colors">
            <Upload className="w-4 h-4" /> Import Curios (CSV)
          </Link>
          <button onClick={exportCurios} disabled={exporting} data-testid="export-curios-btn" className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm text-[#4A4036] hover:border-[#9E4535] hover:text-[#9E4535] transition-colors disabled:opacity-50">
            <Download className="w-4 h-4" /> {exporting ? "Exporting…" : "Export Curios"}
          </button>
          <button onClick={runDuplicateCheck} disabled={checking} data-testid="duplicate-check-btn" className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm text-[#4A4036] hover:border-[#9E4535] hover:text-[#9E4535] transition-colors disabled:opacity-50">
            <Copy className="w-4 h-4" /> {checking ? "Checking…" : "Curio Duplicate Check"}
          </button>
        </div>

        {dupes && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDupes(null)} data-testid="curio-dupes-modal">
            <div className="bg-[#FAF7F0] border border-[#D8CEB9] rounded-sm max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-1">
                <h2 className="font-serif-display text-2xl font-bold text-[#1A1615]">Duplicate Check</h2>
                <button onClick={() => setDupes(null)} className="p-1 text-[#7A6C5D] hover:text-[#9E4535]"><X className="w-5 h-5" /></button>
              </div>
              <p className="font-body text-sm text-[#7A6C5D] mb-4">
                Scanned {dupes.total_curios} curios · <strong>{dupes.duplicate_pairs}</strong> possible duplicate pair{dupes.duplicate_pairs === 1 ? "" : "s"} flagged.
              </p>
              {dupes.pairs.length === 0 && <p className="font-body text-sm text-[#2F6B33]">No likely duplicates found. Your catalogue looks clean.</p>}
              {dupes.pairs.map((p, i) => (
                <div key={i} className="border border-[#D8CEB9] rounded-sm p-3 mb-3" data-testid={`dup-pair-${i}`}>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {p.reasons.map((r, k) => (
                      <span key={k} className="font-mono-cv text-[10px] bg-[#FBF1DD] border border-[#E4D3A8] text-[#8A5A00] px-1.5 py-0.5 rounded-full">{r}</span>
                    ))}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[p.a, p.b].map((c, k) => (
                      <div key={k} className="bg-white border border-[#EAE2D2] rounded-sm p-2">
                        <p className="font-serif-display text-sm font-semibold text-[#1A1615]">{c.title}</p>
                        <p className="font-mono-cv text-[10px] text-[#7A6C5D] mb-1.5">{c.subject_slug}</p>
                        <div className="flex gap-2">
                          <Link to={`/curio/${c.slug}`} target="_blank" className="font-ui text-xs text-[#9E4535] hover:underline">View</Link>
                          <Link to={`/admin/edit/${c.slug}`} className="font-ui text-xs text-[#9E4535] hover:underline">Edit</Link>
                          <button onClick={() => deleteCurio(c.slug)} data-testid={`dup-delete-${c.slug}`} className="font-ui text-xs text-[#8A1E1E] hover:underline">Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <p className="font-body text-[#7A6C5D]">Loading the catalogue…</p>
        ) : (
          <div className="bg-white border border-[#D8CEB9] rounded-sm divide-y divide-[#EAE2D2]" data-testid="facts-list">
            {facts.map((f) => (
              <div key={f.slug} className="flex items-center gap-4 p-4" data-testid={`fact-row-${f.slug}`}>
                <div className="w-14 h-14 shrink-0 overflow-hidden rounded-sm bg-[#F3EDE0]">
                  <img src={f.image_url || undefined} alt={`${f.title} — AI illustration`} className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-serif-display text-base font-semibold text-[#1A1615] truncate">{f.title}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="font-mono-cv text-[10px] text-[#7A6C5D]">{f.subject_slug}</span>
                    <span className="font-mono-cv text-[10px] text-[#B8AC9C]">· {f.sources?.length || 0} sources · {f.tags?.length || 0} tags</span>
                    <ComplianceBadge compliance={f.compliance} />
                  </div>
                  <div className="mt-2">
                    <SocialPost caption={f.social_caption} url={`${window.location.origin}/curio/${f.slug}`} variant="inline" />
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Link to={`/curio/${f.slug}`} target="_blank" className="p-2 text-[#7A6C5D] hover:text-[#9E4535]" title="View" data-testid={`view-${f.slug}`}>
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/admin/edit/${f.slug}`}
                    data-testid={`edit-${f.slug}`}
                    className="inline-flex items-center gap-1.5 font-ui text-xs font-semibold px-3 py-2 border border-[#D8CEB9] rounded-sm text-[#4A4036] hover:border-[#9E4535] hover:text-[#9E4535] transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminFacts;
